import * as React from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import { AxiosResponse } from 'axios';
import { useAppDispatch } from '../../../../hooks';
import catalogApiClient from '../../../../services/catalogapi';
import { alertError } from '../../../../slices/global/alertSlice';

export interface PartTypeProps {
  partTypeId: number;
  partType: string;
}

interface Props {
  value: PartTypeProps | null;
  setValue: React.Dispatch<React.SetStateAction<PartTypeProps | null>>;
  handleChange: (
    event: React.SyntheticEvent<Element, Event>,
    newValue: PartTypeProps | null,
  ) => void;
}

export default function PartTypeAutoComplete({ value, setValue, handleChange }: Props) {
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState<readonly PartTypeProps[]>([]);
  const [inputValue, setInputValue] = React.useState<string>('');
  const loading = open && options.length === 0;
  const dispatch = useAppDispatch();

  React.useEffect(() => {
    const controller = new AbortController();

    if (!loading) {
      return undefined;
    }

    if (inputValue.length < 3) {
      return;
    }

    (async () => {
      console.log('getPartTypes');
      await catalogApiClient
        .get('api/v1/fitment/package/GetPartTypes', {
          signal: controller.signal,
        })
        .then((response: AxiosResponse<PartTypeProps[]>) => {
          let filtered = response.data.filter(
            (x) =>
              x.partType.toLowerCase().includes(inputValue.toLowerCase()) ||
              x.partTypeId.toString().includes(inputValue.toLowerCase()),
          );
          setOptions([...filtered]);
        })
        .catch(function (err) {
          if (err.message !== 'canceled')
            dispatch(alertError(`Failed to retrieve edi files. Reason: ${err}`));
        });
    })();

    return () => {
      controller.abort();
    };
  }, [loading, dispatch, inputValue]);

  React.useEffect(() => {
    if (!open) {
      setOptions([]);
    }
  }, [open]);

  return (
    <Autocomplete
      disablePortal
      value={value}
      open={open}
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
      }}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={(_, newInputValue) => {
        setInputValue(newInputValue);
      }}
      id="class-autocomplete"
      options={options}
      renderOption={(props, option) => <li {...props}>{option.partType}</li>}
      getOptionLabel={(option) => option.partType ?? ''}
      isOptionEqualToValue={(option, value) => option.partTypeId === value.partTypeId}
      sx={{ width: 500 }}
      renderInput={(params) => <TextField {...params} label="Part Type" />}
    />
  );
}
